import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

const FONT_SIZE_KEY = 'varnakrama_font_size_level';

// Font size levels: 1-10, where 5 is the default (1.0x)
// Level 1 = 0.7x, Level 10 = 1.6x
const MIN_LEVEL = 1;
const MAX_LEVEL = 10;
const DEFAULT_LEVEL = 5;

const getMultiplierFromLevel = (level: number): number => {
  // Map level 1-10 to multiplier 0.7-1.6
  // Level 5 = 1.0 (default)
  return 0.7 + (level - 1) * 0.1;
};

interface SettingsContextType {
  fontSizeLevel: number;
  setFontSizeLevel: (level: number) => void;
  scaledFontSize: (baseSize: number) => number;
  minLevel: number;
  maxLevel: number;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [fontSizeLevel, setFontSizeLevelState] = useState<number>(DEFAULT_LEVEL);

  useEffect(() => {
    const savedLevel = localStorage.getItem(FONT_SIZE_KEY);
    if (savedLevel) {
      const level = parseInt(savedLevel, 10);
      if (level >= MIN_LEVEL && level <= MAX_LEVEL) {
        setFontSizeLevelState(level);
      }
    }
  }, []);

  const setFontSizeLevel = (level: number) => {
    const clampedLevel = Math.max(MIN_LEVEL, Math.min(MAX_LEVEL, level));
    localStorage.setItem(FONT_SIZE_KEY, clampedLevel.toString());
    setFontSizeLevelState(clampedLevel);
  };

  const scaledFontSize = (baseSize: number): number => {
    const multiplier = getMultiplierFromLevel(fontSizeLevel);
    return Math.round(baseSize * multiplier);
  };

  const value: SettingsContextType = {
    fontSizeLevel,
    setFontSizeLevel,
    scaledFontSize,
    minLevel: MIN_LEVEL,
    maxLevel: MAX_LEVEL,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
